import React from "react";
import { Badge } from "react-bootstrap";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { FormatHelper, ServiceHelper } from "../../../assets/utils/helpers";
import { BannersGridDto } from "../../../services/dto";

const renderAreaPills = (
    locations: string | string[] | undefined | null
): React.JSX.Element | null => {
    const values =
        typeof locations === "string"
            ? locations.split(",").map(location => location.trim())
            : Array.isArray(locations)
                ? locations.map(location => location.trim())
                : [];

    const filteredValues = values.filter(location => location.length > 0);

    if (filteredValues.length === 0) return null;

    return (
        <div className="d-flex flex-wrap gap-2">
            {filteredValues.map((location: string, index: number) => (
                <Badge
                    key={`${location}-${index}`}
                    pill
                    bg="transparent"
                    text="dark"
                    className="border border-dark px-3 py-2"
                >
                    {location}
                </Badge>
            ))}
        </div>
    );
};

// These cells render once via renderToString (see FormatCustomCell in
// controlDataGrid.tsx) and are handed to DataTables/jQuery as static HTML,
// outside React's render tree -- no hooks or React event-handler props here.
// See the matching comment in referenceGrid.tsx for the full explanation.
const escapeHtmlAttribute = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const renderBannerImageCell = (
    id: string,
    rowData: BannersGridDto
): React.JSX.Element => {
    const boxStyle: React.CSSProperties = {
        width: 240,
        height: 80,
        borderRadius: 6,
        overflow: "hidden",
        border: "1px solid var(--color-divider-subtle, #e5e7eb)",
        background: "var(--color-background-light, #f8f9fa)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    if (!rowData?.imagePath) {
        return (
            <div style={boxStyle}>
                <i
                    className="bi bi-image"
                    style={{ color: "var(--color-icon-muted)", fontSize: 22 }}
                />
            </div>
        );
    }

    const src = escapeHtmlAttribute(ServiceHelper.GetImageUrl(rowData.imagePath));
    const alt = escapeHtmlAttribute(rowData?.name || "Image");
    const html =
        `<img src="${src}" alt="${alt}" ` +
        `style="width:100%;height:100%;object-fit:fill;display:block;" ` +
        `onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` +
        `<i class="bi bi-image" style="display:none;color:var(--color-icon-muted);font-size:22px;"></i>`;

    return <div style={boxStyle} dangerouslySetInnerHTML={{ __html: html }} />;
};

const renderBannerDetailsCell = (
    id: string,
    rowData: BannersGridDto
): React.JSX.Element => (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="vfx-datagrid-resource-name">
            {rowData.name ?? "Untitled"}
        </span>
        {rowData.description && (
            <span className="vfx-datagrid-resource-description" style={{ margin: 0 }}>
                {rowData.description}
            </span>
        )}
        {renderAreaPills(rowData.locations)}
    </div>
);

const renderDateRangeCell = (
    id: string,
    rowData: BannersGridDto
): React.JSX.Element | null => {
    const startDate = rowData?.startDate
        ? FormatHelper.formatDate(rowData.startDate)
        : null;
    const endDate = rowData?.endDate
        ? FormatHelper.formatDate(rowData.endDate)
        : null;

    if (!startDate && !endDate) return null;

    if (startDate && endDate) {
        return (
            <span
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    columnGap: 8,
                    whiteSpace: "nowrap",
                    width: "100%",
                }}
            >
                <span style={{ textAlign: "left" }}>{startDate}</span>
                <span style={{ textAlign: "center" }}>-</span>
                <span style={{ textAlign: "right" }}>{endDate}</span>
            </span>
        );
    }

    return <span style={{ whiteSpace: "nowrap" }}>{startDate ?? `End: ${endDate}`}</span>;
};

export const createBannerGridColumns = (): ControlDataGridColumnProps[] => [
    {
        data: "id",
        title: "ID",
        visible: false,
        columnType: "number",
        alignment: "right",
    },
    {
        data: "sortOrder",
        title: "Order",
        visible: true,
        sortable: false,
        columnType: "text",
        alignment: "center",
        width: "5%",
    },
    {
        data: "imagePath",
        title: "Banner Image",
        visible: true,
        columnType: "custom",
        alignment: "left",
        sortable: false,
        width: "14%",
        customCell: renderBannerImageCell,
    },
    {
        data: "name",
        title: "Resource",
        visible: true,
        columnType: "custom",
        alignment: "left",
        sortable: true,
        width: "46%",
        customCell: renderBannerDetailsCell,
    },
    {
        data: "startDate",
        title: "Dates",
        visible: true,
        columnType: "custom",
        alignment: "left",
        sortable: true,
        width: "14%",
        customCell: renderDateRangeCell,
    },
];